<template>
    <div class="about-us">
        <div class="page-header">
            <h2 class="page-title">关于我们</h2>
            <p class="page-desc">配置公司信息，展示在关于我们页面</p>
        </div>

        <el-card shadow="never">
            <el-form :model="aboutForm" label-width="120px" v-loading="loading">
                <el-form-item label="公司名称" required>
                    <el-input v-model="aboutForm.company_name" placeholder="请输入公司名称" />
                    <div class="form-tip">请填写完整的公司名称</div>
                </el-form-item>

                <el-form-item label="公司LOGO">
                    <div class="avatar-upload-container">
                        <el-input v-model="aboutForm.company_logo" placeholder="请输入公司LOGO URL" class="form-input" />
                        <el-button type="success" @click="handleUploadAvatar">
                            <el-icon><Upload /></el-icon> 上传LOGO
                        </el-button>
                    </div>
                    <div v-if="aboutForm.company_logo" class="logo-preview">
                        <el-image :src="aboutForm.company_logo" fit="contain" style="width: 100px; height: 100px;" />
                    </div>
                    <div class="form-tip">建议尺寸：200x200像素，支持jpg、png、gif格式</div>
                </el-form-item>

                <el-form-item label="公司介绍">
                    <el-input type="textarea" v-model="aboutForm.company_desc" placeholder="请输入公司介绍" rows="6" />
                    <div class="form-tip">详细介绍公司的业务、文化、发展历程等</div>
                </el-form-item>

                <el-form-item label="公司地址">
                    <el-input v-model="aboutForm.company_address" placeholder="请输入公司地址" />
                    <div class="form-tip">公司的详细地址，便于客户联系</div>
                </el-form-item>

                <el-form-item label="负责人姓名">
                    <el-input v-model="aboutForm.responsible_person" placeholder="请输入负责人姓名" />
                </el-form-item>

                <el-form-item label="联系方式">
                    <el-input v-model="aboutForm.contact_number" placeholder="请输入联系方式" />
                    <div class="form-tip">建议填写公司电话或客服热线</div>
                </el-form-item>

                <el-form-item>
                    <el-button type="primary" @click="saveAboutInfo" :loading="saving">
                        <el-icon><Check /></el-icon>保存配置
                    </el-button>
                    <el-button @click="fetchAboutInfo" :loading="loading">刷新</el-button>
                </el-form-item>
            </el-form>
        </el-card>
    </div>
</template>

<script>
import axios from 'axios';
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { Check, Upload } from '@element-plus/icons-vue';
import { auth } from '@/utils/auth.js';
import config from '@/config.js';
import { beforeUpload, uploadToOSS, getUploadSignature } from '@/utils/uploadUtils';

export default {
    name: 'AboutUs',
    components: {
        Check,
        Upload
    },
    setup() {
        const router = useRouter();
        const loading = ref(false);
        const saving = ref(false);
        
        const aboutForm = reactive({
            id: 0,
            agent_id: 0,
            host_url: '',
            company_name: '',
            company_logo: '',
            company_desc: '',
            company_address: '',
            responsible_person: '',
            contact_number: ''
        });

        // 获取当前登录用户
        const getCurrentUser = () => {
            const user = auth.getStoredUser();
            if (!user) {
                ElMessage.error('未登录，请重新登录');
                router.push('/login');
                return null;
            }
            return user;
        };

        // 上传LOGO
        const handleUploadAvatar = async () => {
            try {
                const fileInput = document.createElement('input');
                fileInput.type = 'file';
                fileInput.accept = 'image/*';
                fileInput.onchange = async (event) => {
                    const file = event.target.files[0];
                    if (!file) return;

                    const isValid = await beforeUpload(file, {
                        maxSize: 5,
                        allowedTypes: ['image/jpeg', 'image/png', 'image/gif'],
                    });
                    if (!isValid) return;

                    const signatureData = await getUploadSignature('image');
                    if (!signatureData) {
                        ElMessage.error("获取上传签名失败");
                        return;
                    }

                    const uploadResult = await uploadToOSS(file, signatureData);
                    if (uploadResult.success) {
                        ElMessage.success("LOGO上传成功");
                        aboutForm.company_logo = uploadResult.url;
                    } else {
                        ElMessage.error("LOGO上传失败");
                    }
                };
                fileInput.click();
            } catch (error) {
                console.error("上传LOGO失败:", error);
                ElMessage.error("上传LOGO失败，请重试");
            }
        };

        // 获取关于我们信息
        const fetchAboutInfo = async () => {
            const user = getCurrentUser();
            if (!user) return;
            
            loading.value = true;
            try {
                const response = await axios.post(`${config.BASE_URL}/agent/api/about_api.php`, {
                    action: 'get',
                    agent_id: user.id,
                    host_url: config.BASE_URL
                });
                
                if (response.data.success && response.data.data) {
                    const data = response.data.data;
                    aboutForm.id = data.id || 0;
                    aboutForm.agent_id = data.agent_id || user.id;
                    aboutForm.host_url = data.host_url || config.BASE_URL;
                    aboutForm.company_name = data.company_name || '';
                    aboutForm.company_logo = data.company_logo || '';
                    aboutForm.company_desc = data.company_desc || '';
                    aboutForm.company_address = data.company_address || '';
                    aboutForm.responsible_person = data.responsible_person || '';
                    aboutForm.contact_number = data.contact_number || '';
                } else {
                    ElMessage.error(response.data.message || '获取信息失败');
                }
            } catch (error) {
                console.error('获取关于我们信息失败:', error);
                ElMessage.error('网络错误，请稍后重试');
            } finally {
                loading.value = false;
            }
        };

        // 保存关于我们信息
        const saveAboutInfo = async () => {
            const user = getCurrentUser();
            if (!user) return;
            
            saving.value = true;
            try {
                const response = await axios.post(`${config.BASE_URL}/agent/api/about_api.php`, {
                    action: 'save',
                    agent_id: user.id,
                    host_url: aboutForm.host_url || config.BASE_URL,
                    company_name: aboutForm.company_name,
                    company_logo: aboutForm.company_logo,
                    company_desc: aboutForm.company_desc,
                    company_address: aboutForm.company_address,
                    responsible_person: aboutForm.responsible_person,
                    contact_number: aboutForm.contact_number
                });
                
                if (response.data.success) {
                    ElMessage.success('保存成功');
                    fetchAboutInfo(); // 刷新数据
                } else {
                    ElMessage.error(response.data.message || '保存失败');
                }
            } catch (error) {
                console.error('保存关于我们信息失败:', error);
                ElMessage.error('保存失败，请检查网络连接');
            } finally {
                saving.value = false;
            }
        };

        onMounted(() => {
            fetchAboutInfo();
        });

        return {
            loading,
            saving,
            aboutForm,
            handleUploadAvatar,
            fetchAboutInfo,
            saveAboutInfo
        };
    }
};
</script>

<style scoped>
.about-us {
    padding: 0px;
}

.page-header {
    margin-bottom: 20px;
}

.page-title {
    font-size: 24px;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 4px;
}

.page-desc {
    font-size: 14px;
    color: #8c8c8c;
}

.form-tip {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
    line-height: 1.4;
}

.avatar-upload-container {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 10px;
    margin-bottom: 10px;
}

.avatar-upload-container .el-input {
    flex: 1;
}

.logo-preview {
    margin-top: 10px;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    padding: 10px;
    display: inline-block;
}
</style>